/**
 * nosto-cart-sync — keeps Nosto's backend cart state in sync with
 * Shopify after every cart mutation, so recommendations stay
 * cart-aware and add-to-cart / cart-updated events fire on Nosto's side.
 *
 * Two-tier strategy
 * -----------------
 *   1. Prefer `Nosto.reloadCart()` — the official Shopify-Nosto bridge
 *      helper exposed on `window.Nosto`. It refreshes the cart from
 *      Shopify and triggers the right backend events (visible in the
 *      toolbar's Visitor → Session → Cart panel). This is the only path
 *      that registers an explicit add-to-cart event. Docs:
 *      https://docs.nosto.com/shopify/features/add-to-cart
 *
 *   2. If `Nosto.reloadCart` is unavailable (older versions of the
 *      bridge), fall back to a direct `setCart()` push on the default
 *      session and a `loadRecommendations()` refresh.
 *
 * Trigger: the theme already fires `cart:change` on every cart mutation
 * (see theme.js — product-form, cart-items, etc.). We subscribe and
 * debounce to coalesce rapid back-to-back events.
 *
 * Load order: deferred in `layout/theme.liquid` only when Nosto is
 * active. The subscription registers before any cart action can fire,
 * thanks to the `cart:change` listener being attached at parse time.
 */
(function () {
  if (typeof window === 'undefined') return;
  if (window.__nostoCartSyncInstalled) return;
  window.__nostoCartSyncInstalled = true;

  var DEBOUNCE_MS = 250;
  var pending = null;

  function syncToNosto() {
    if (typeof window.nostojs !== 'function') return;

    // Tier 1: Shopify-Nosto bridge helper (preferred).
    if (window.Nosto && typeof window.Nosto.reloadCart === 'function') {
      Promise.resolve(window.Nosto.reloadCart()).catch(function (err) {
        console.warn('[NostoCartSync] Nosto.reloadCart failed:', err);
      });
      return;
    }

    // Tier 2: direct setCart() fallback. Fetch /cart.js and push the
    // mapped items to Nosto's default session so recommendations refresh
    // with up-to-date context.
    fetchCart()
      .then(function (cart) {
        if (!cart) return;
        var nostoCart = toNostoCart(cart);
        window.nostojs(function (api) {
          var session = api.defaultSession();
          if (!session || typeof session.setCart !== 'function') return;
          session
            .setCart(nostoCart)
            .load()
            .finally(function () {
              if (typeof api.loadRecommendations === 'function') {
                api.loadRecommendations();
              }
            });
        });
      })
      .catch(function (err) {
        console.warn('[NostoCartSync] setCart fallback failed:', err);
      });
  }

  function fetchCart() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      if (!r.ok) return null;
      return r.json();
    });
  }

  function toNostoCart(cart) {
    var currency =
      (cart && cart.currency) ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'EUR';
    var items = (cart && Array.isArray(cart.items) ? cart.items : []).map(function (item) {
      return {
        product_id: String(item.product_id),
        sku_id: String(item.variant_id),
        name: item.product_title || item.title || '',
        quantity: Number(item.quantity) || 1,
        unit_price: (Number(item.final_price) || Number(item.price) || 0) / 100,
        price_currency_code: currency,
      };
    });
    return { items: items };
  }

  function scheduleSync() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      syncToNosto();
    }, DEBOUNCE_MS);
  }

  document.addEventListener('cart:change', scheduleSync);
  document.addEventListener('cart:refresh', scheduleSync);
})();
